/**
 * Funds Deposit has added but the shopper hasn't collected yet: the chrome's balance stays at what
 * it was before them until Deposit closes and its trail lands in the balance pill
 * (`balanceTrail.ts` in the design system), so the pill rolls up as the funds arrive rather than
 * before the shopper has seen them added. Only what the chrome shows; every other balance, and
 * every guard, reads the server's.
 */
import { useSyncExternalStore } from "react";

const listeners = new Set<() => void>();
let before: number | null = null;
const store = {
  get: () => before,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

function set(cents: number | null): void {
  if (before === cents) return;
  before = cents;
  for (const listener of listeners) listener();
}

/** Funds are being added: the chrome shows `cents`, the spendable balance without them. */
export function holdBalanceAt(cents: number): void {
  set(Math.max(0, cents));
}

/** The funds reached the balance: the chrome shows the server's again. */
export function collectFunds(): void {
  set(null);
}

/** The balance the chrome shows while funds wait to be collected; null otherwise. */
export function useBalanceBeforeFunds(): number | null {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}
